import { Injectable } from '@angular/core';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';
import { Braintree } from '@df/module-braintree/braintree.namespace';

@Injectable({
    providedIn: 'root'
})
export class BraintreeService {
    generalErrorMessage = Checkout.GENERAL_PAYMENT_ERROR_MESSAGE;

    /**
     * We need component id for hosted fields
     * We can have more than one hosted fields instance
     * And we have to select fields by html id selector
     * @type {number}
     */
    private componentId = 1;

    /**
     * Returns id for hosted fields component
     * @returns {number}
     */
    getComponentId(): number {
        return this.componentId++;
    }

    constructor(private requestService: RequestService) {}

    //////////////////////////////////////////
    /////
    /////   Client methods
    /////
    //////////////////////////////////////////

    get braintree() {
        return typeof window === 'undefined' ? undefined : window.braintree;
    }

    /**
     * Returns braintree client constructor
     * @returns {Braintree.IClient}
     */
    getClient(): Braintree.IClient | undefined {
        return this.braintree?.client;
    }

    //////////////////////////////////////////
    /////
    /////   Hosted fields methods
    /////
    //////////////////////////////////////////

    /**
     * Returns error message from Braintree.IError for tokenization error
     * @param {Braintree.IError} error
     * @returns {string}
     */
    getTokenizeHostedFieldsErrorMessage(error: Braintree.IError): string {
        switch (error.code) {
            case 'HOSTED_FIELDS_FIELDS_EMPTY':
                // occurs when none of the fields are filled in
                return 'All payment form fields are empty. Please fill out the payment form.';
            case 'HOSTED_FIELDS_FIELDS_INVALID':
                // occurs when certain fields do not pass client side validation
                const invalidFieldKeys = error.details && error.details.invalidFieldKeys;
                return `Some payment input fields are invalid ${
                    invalidFieldKeys && invalidFieldKeys.length ? `: ${invalidFieldKeys.join(', ')}.` : '.'
                }`;
            case 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE':
                // occurs when:
                //   * the client token used for client authorization was generated
                //     with a customer ID and the fail on duplicate payment method
                //     option is set to true
                //   * the card being tokenized has previously been vaulted (with any customer)
                // See: https://developers.braintreepayments.com/reference/request/client-token/generate/#options.fail_
                // on_duplicate_payment_method
                return 'This payment method already exists in your vault.';
            case 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED':
                // occurs when:
                //   * the client token used for client authorization was generated
                //     with a customer ID and the verify card option is set to true
                //     and you have credit card verification turned on in the Braintree
                //     control panel
                // tslint:disable-next-line: max-line-length
                //   * the cvv does not pass verfication (https://developers.braintreepayments.com/reference/general/testing/#avs-and-cvv/cid-responses)
                // See: https://developers.braintreepayments.com/reference/request/client-token/generate/#options.verify_card
                return 'CVV did not pass verification';
            case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
                // occurs for any other tokenization error on the server
                return 'Tokenization failed server side. Is the card valid?';
            case 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR':
                // occurs when the Braintree gateway cannot be contacted
                return 'Network error occurred when tokenizing.';
            default:
                return this.generalErrorMessage;
        }
    }

    /**
     * Returns braintree hosted-fields constructor
     * @returns {Braintree.IHostedFields}
     */
    getHostedFields(): Braintree.IHostedFields | undefined {
        return this.braintree?.hostedFields;
    }

    //////////////////////////////////////////
    /////
    /////   Three DS methods
    /////
    //////////////////////////////////////////

    /**
     * Returns braintree three ds concstructor
     * @returns {Braintree.IThreeDS}
     */
    getThreeDS(): Braintree.IThreeDS | undefined {
        return this.braintree?.threeDSecure;
    }

    //////////////////////////////////////////
    /////
    /////   Paypal methods
    /////
    //////////////////////////////////////////

    /**
     * Returns braintree paypal checkout constructor
     */
    getPaypalCheckout(): Braintree.IPaypalCheckout | undefined {
        return this.braintree?.paypalCheckout;
    }

    /**
     * Returns error message from Braintree.IError for paypal checkout error
     * @param {Braintree.IError} error
     * @returns {string}
     */
    getPaypalCheckoutErrorMessage(error: Braintree.IError): string {
        switch (error?.code) {
            case 'PAYPAL_INVALID_PAYMENT_OPTION':
                return 'Please check your shipping address';
            default:
                return this.generalErrorMessage;
        }
    }

    //////////////////////////////////////////
    /////
    /////   Data collector methods
    /////
    //////////////////////////////////////////

    /**
     * Returns data collector constructor
     */
    getDataCollector(): Braintree.IDataCollector | undefined {
        return this.braintree?.dataCollector;
    }

    //////////////////////////////////////////
    /////
    /////   Local payment methods
    /////
    //////////////////////////////////////////

    /**
     * Returns local payment contructor
     * @returns {Braintree.ILocalPayment | undefined}
     */
    getLocalPayment(): Braintree.ILocalPayment | undefined {
        return this.braintree?.localPayment;
    }

    /**
     * Checks if passed methodCode is braintree local payment method
     * @param {string} methodCode
     * @returns {boolean}
     */
    isLocalPaymentMethod(methodCode: string): boolean {
        const localPaymentMethods: string[] = Object.values(Braintree.LocalPaymentMethods);
        return localPaymentMethods.indexOf(methodCode) > -1;
    }

    /**
     * Returns local payment type from local payment method
     * @param {Braintree.LocalPaymentMethods} localPaymentMethod
     * @returns {Braintree.LocalPaymentTypes | undefined}
     */
    getLocalPaymentType(localPaymentMethod: Braintree.LocalPaymentMethods): Braintree.LocalPaymentTypes | undefined {
        const parts = localPaymentMethod.split('__');
        return parts.length === 2 ? (parts[1] as Braintree.LocalPaymentTypes) : undefined;
    }

    //////////////////////////////////////////
    /////
    /////   Vault nonce methods
    /////
    //////////////////////////////////////////

    getVaultNonce(publicHash: string, status?: Status): Promise<Braintree.IVaultPaymentMethodNonce> {
        return this.requestService.send(status, { path: Braintree.Endpoints.GET_NONCE, data: { public_hash: publicHash } });
    }

    updateVaultNonce(publicHash: string, nonce: string, status?: Status): Promise<any> {
        return this.requestService.send(status, { path: Braintree.Endpoints.UPDATE_NONCE, data: { nonce, public_hash: publicHash } });
    }

    //////////////////////////////////////////
    /////
    /////   Apple pay methods
    /////
    //////////////////////////////////////////

    getApplePay(): Braintree.IApplePay | undefined {
        return this.braintree?.applePay;
    }

    //////////////////////////////////////////
    /////
    /////   Google pay methods
    /////
    //////////////////////////////////////////

    getGooglePay(): Braintree.IGooglePay | undefined {
        return this.braintree?.googlePayment;
    }
}
